import { Separator } from '@/ui/components/separator/separator.styles'
import RemoveIcon from '@assets/images/remove.svg?react'
import TechnologyIcon from '@assets/images/technology-icon.svg?react'
import { CustomSelect } from '@atbay/component-lib'
import { useCustomTheme } from '@/core/theme'
import Fuse from 'fuse.js'
import { FC, Fragment } from 'react'
import {
    Body,
    Flex,
    InputWrapper,
    SelectedTechnologiesWrapper,
    SelectedTechnology,
    StyledSearchIcon,
    Title,
} from './addTechnology.styles'
import Typography from '@/ui/components/typography'

type TAddTechnologyProps = {
    selectedTechnologies: TTechnologyMap
    onRemoveTechnology: (value: TTechnologyOption) => void
    onSelectTechnology: (value: SelectionOption) => void
    technologiesOptions: TTechnologyOption[]
    isLoading: boolean
}

const AddTechnology: FC<TAddTechnologyProps> = ({
    selectedTechnologies,
    onRemoveTechnology,
    onSelectTechnology,
    technologiesOptions,
    isLoading,
}) => {
    const selectedTechnologiesArray = Object.values(selectedTechnologies)
    const { colors } = useCustomTheme()
    const defaultValue = { value: '', id: -1 }

    const options = {
        keys: ['value'],
    }

    const fuse = useMemo(
        () => new Fuse(technologiesOptions, options),
        [technologiesOptions, options]
    )

    const filterFunc = (options: SelectionOption[], state: unknown) => {
        // @ts-ignore
        const search = state?.inputValue
        if (search) {
            const results = fuse.search(search).map(({ item }) => item)
            return results
        }
        return options
    }

    return (
        <Body data-testid="add-technology">
            <Title component="p" variant="l-regular">
                Add Technologies
            </Title>
            <p className="note">
                Search and add any technologies you use that we haven't
                detected. This ensures your security monitoring covers all your
                tools and platforms.
            </p>
            <InputWrapper>
                <CustomSelect
                    disabled={isLoading}
                    clearIconStyles={{
                        backgroundColor: 'transparent',
                        color: colors.greys[400],
                        marginRight: '18px',
                        marginBottom: '7px',
                    }}
                    className="autocomplete"
                    options={technologiesOptions}
                    blurOnSelect={true}
                    filterOptionsFunc={filterFunc}
                    selectedValue={defaultValue}
                    onChange={onSelectTechnology}
                    label=""
                    extraInputProps={{
                        placeholder: 'Enter technology name',
                        disableUnderline: true,
                    }}
                    defaultValue={defaultValue}
                    accessibilityId="primary-custom-single-select"
                />
                <StyledSearchIcon />
            </InputWrapper>
            {selectedTechnologiesArray.length > 0 && <Separator />}
            <SelectedTechnologiesWrapper data-testid="selected-technologies">
                {selectedTechnologiesArray.map((technology) => (
                    <Fragment key={technology.id}>
                        <SelectedTechnology>
                            <Flex>
                                <TechnologyIcon width={10} height={10} />
                                <Typography variant="s-regular">
                                    {technology.value}
                                </Typography>
                            </Flex>
                            {!isLoading && (
                                <RemoveIcon
                                    className="remove-icon"
                                    data-testid="remove-icon"
                                    onClick={() =>
                                        onRemoveTechnology(technology)
                                    }
                                    width={10}
                                    height={10}
                                />
                            )}
                        </SelectedTechnology>
                    </Fragment>
                ))}
            </SelectedTechnologiesWrapper>
        </Body>
    )
}

export default AddTechnology
